/**
 * Line margin display states.
 *
 * "What is this line's margin" has three genuinely different answers, and the old
 * `line.cogs > 0` test collapsed them into two — treating both "not costed yet"
 * and "no cost exists" as the same blank.
 *
 * The dangerous case is the third. A manual line billed without a cost has
 * `cogs = 0` and therefore `gross_profit === total_price`, which renders as full
 * revenue — a 100% margin that is not real. `cogs_source` exists precisely to
 * distinguish it, and its own doc comment says not to render it as margin.
 *
 * Note also that since order discounts are apportioned into line margin,
 * `gross_profit` is no longer `total_price - cogs`: it is the line's share of
 * discounted revenue less its cost. That is the figure reports aggregate, and the
 * reason no per-line percentage is offered here — the correct denominator is the
 * line's apportioned revenue, which the payload does not carry. Percentages
 * belong at order level, once.
 */

import type { SOLine } from "@/types/sales-order";

export type MarginState =
  /** No cost was ever recorded, so margin is not computable. Never show a figure. */
  | "none"
  /** Costed, but not yet locked — COGS snapshots when the order is confirmed. */
  | "pending"
  /** Locked at confirmation; the numbers are real. */
  | "known";

export interface LineMargin {
  state: MarginState;
  cogs: number | null;
  grossProfit: number | null;
  /** Why the cells are empty, for a title attribute. Null when they are not. */
  explanation: string | null;
}

export function lineMargin(line: SOLine, orderStatus: string): LineMargin {
  if (line.cogs_source === "none") {
    return {
      state: "none",
      cogs: null,
      grossProfit: null,
      explanation:
        "No cost recorded for this line, so its margin cannot be computed.",
    };
  }

  if (orderStatus === "Draft") {
    return {
      state: "pending",
      cogs: null,
      grossProfit: null,
      explanation: "Cost is locked in when the sale is confirmed.",
    };
  }

  return {
    state: "known",
    cogs: Number(line.cogs),
    grossProfit: Number(line.gross_profit),
    explanation: null,
  };
}

export interface OrderMargin {
  /** Summed over lines whose margin is known. Null when none are. */
  grossProfit: number | null;
  /** Against discounted revenue, which is what the margin was earned on. */
  percent: number | null;
  /** True when some lines carry no cost, so the total understates nothing but omits them. */
  partial: boolean;
}

/**
 * Roll line margins up to the order.
 *
 * Lines with no recorded cost are excluded rather than counted as pure profit,
 * and their presence is reported so the figure can be labelled as covering only
 * part of the order.
 */
export function orderMargin(
  lines: SOLine[],
  orderStatus: string,
  discountedSubtotal: number,
): OrderMargin {
  const margins = lines.map((line) => lineMargin(line, orderStatus));
  const known = margins.filter((m) => m.state === "known");

  if (!known.length) {
    return {
      grossProfit: null,
      percent: null,
      partial: margins.some((m) => m.state === "none"),
    };
  }

  const grossProfit = known.reduce((sum, m) => sum + (m.grossProfit ?? 0), 0);
  return {
    grossProfit,
    percent: discountedSubtotal > 0 ? grossProfit / discountedSubtotal : null,
    partial: margins.some((m) => m.state === "none"),
  };
}
